import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Store as StoreIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../contexts/AuthContext';

const Vendors = () => {
    const { user } = useAuth();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        gst_number: '',
        credit_period: 0
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchVendors();
    }, []);

    const fetchVendors = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/vendors`);
            const data = await response.json();
            if (data.success) {
                setVendors(data.data);
            }
        } catch (error) {
            console.error('Error fetching vendors:', error);
            toast.error('Failed to load vendors');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (vendor = null) => {
        if (vendor) {
            setFormData({
                name: vendor.name,
                contact_person: vendor.contact_person || '',
                email: vendor.email || '',
                phone: vendor.phone || '',
                address: vendor.address || '',
                gst_number: vendor.gst_number || '',
                credit_period: vendor.credit_period || 0
            });
            setEditingId(vendor.id);
        } else {
            setFormData({
                name: '',
                contact_person: '',
                email: '',
                phone: '',
                address: '',
                gst_number: '',
                credit_period: 0
            });
            setEditingId(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingId(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Vendor name is required');
            return;
        }

        try {
            const url = editingId
                ? `${API_BASE_URL}/vendors/${editingId}`
                : `${API_BASE_URL}/vendors`;

            const method = editingId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message);
                fetchVendors();
                handleCloseDialog();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error('Error saving vendor:', error);
            toast.error('Failed to save vendor');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this vendor?')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/vendors/${id}`, {
                method: 'DELETE'
            });
            const data = await response.json();

            if (data.success) {
                toast.success(data.message);
                fetchVendors();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error('Error deleting vendor:', error);
            toast.error('Failed to delete vendor');
        }
    };

    const filteredVendors = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vendor.contact_person && vendor.contact_person.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (vendor.email && vendor.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StoreIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                        Vendors
                    </Typography>
                </Box>
                {user && user.role !== 'VIEWER' && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            background: 'linear-gradient(45deg, #2e7d32, #60ad5e)'
                        }}
                    >
                        New Vendor
                    </Button>
                )}
            </Box>

            <Paper sx={{ p: 2, borderRadius: 4, mb: 3 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search vendors by name, contact or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 2 }
                    }}
                />
            </Paper>

            <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Vendor Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Contact Person</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Contact Info</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>GST Number</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Credit Period</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        Loading vendors...
                                    </TableCell>
                                </TableRow>
                            ) : filteredVendors.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        No vendors found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredVendors.map((vendor) => (
                                    <TableRow key={vendor.id} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>{vendor.name}</TableCell>
                                        <TableCell>{vendor.contact_person || '-'}</TableCell>
                                        <TableCell>
                                            <Box>
                                                {vendor.email && <div style={{ fontSize: '0.9em' }}>{vendor.email}</div>}
                                                {vendor.phone && <div style={{ fontSize: '0.9em', color: 'text.secondary' }}>{vendor.phone}</div>}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{vendor.gst_number || '-'}</TableCell>
                                        <TableCell>{vendor.credit_period} Days</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={vendor.is_active ? 'Active' : 'Inactive'}
                                                color={vendor.is_active ? 'success' : 'default'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {user && user.role !== 'VIEWER' && (
                                                <>
                                                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(vendor)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(vendor.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {editingId ? 'Edit Vendor' : 'New Vendor'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Vendor Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact Person"
                                name="contact_person"
                                value={formData.contact_person}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="address"
                                multiline
                                rows={2}
                                value={formData.address}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="GST Number"
                                name="gst_number"
                                value={formData.gst_number}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Credit Period (Days)"
                                name="credit_period"
                                type="number"
                                value={formData.credit_period}
                                onChange={handleInputChange}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        sx={{ background: 'linear-gradient(45deg, #2e7d32, #60ad5e)' }}
                    >
                        Save Vendor
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Vendors;
